import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { resolveImage } from "@/lib/image-map";
import { ExternalLink, Eye, EyeOff, Sparkles, Edit3, X, Loader2, ChevronDown } from "lucide-react";
import { generateProductDescription } from "@/lib/ai-product.functions";

type ProductRow = {
  id: string;
  title: string;
  brand: string;
  price: number;
  currency: string;
  category: string;
  network: string;
  in_stock: boolean;
  image_key: string | null;
  url: string;
  description: string | null;
  updated_at: string;
  open_reports?: number;
  link_broken?: boolean;
  link_check_error?: string | null;
  link_last_checked?: string | null;
};

type ProductReport = {
  id: string;
  product_id: string;
  reason: string;
  notes: string | null;
  status: string;
  created_at: string;
};

const REASON_LABEL: Record<string, string> = {
  out_of_stock: "Out of stock",
  wrong_price: "Wrong price shown",
  broken_link: "Broken or incorrect link",
  inappropriate: "Inappropriate content",
  other: "Other",
};

export const Route = createFileRoute("/_authenticated/admin/products")({
  head: () => ({
    meta: [
      { title: "Products — Admin" },
      { name: "description", content: "Manage the affiliate product catalog: search, toggle stock and refresh descriptions." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ProductsAdmin,
});

function fmt(n: number, ccy = "GBP") {
  try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(n); }
  catch { return `£${n.toFixed(0)}`; }
}

function ProductsAdmin() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [stockFilter, setStockFilter] = useState<"all" | "in" | "out">("all");
  const [networkFilter, setNetworkFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [reportsOnly, setReportsOnly] = useState(false);
  const [brokenOnly, setBrokenOnly] = useState(false);
  const [editing, setEditing] = useState<ProductRow | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const categories = useQuery({
    queryKey: ["product-categories"],
    queryFn: async () => {
      const { data, error } = await supabase.from("products").select("category");
      if (error) throw error;
      return [...new Set((data ?? []).map((d) => d.category).filter(Boolean))].sort() as string[];
    },
  });

  const list = useQuery({
    queryKey: ["admin-products", debouncedSearch, stockFilter, networkFilter, categoryFilter, reportsOnly, brokenOnly],
    queryFn: async () => {
      let q = supabase
        .from("products")
        .select("id,title,brand,price,currency,category,network,in_stock,image_key,url,description,updated_at,link_broken,link_check_error,link_last_checked")
        .order("updated_at", { ascending: false })
        .limit(200);
      if (stockFilter === "in") q = q.eq("in_stock", true);
      if (stockFilter === "out") q = q.eq("in_stock", false);
      if (networkFilter !== "all") q = q.eq("network", networkFilter);
      if (categoryFilter !== "all") q = q.eq("category", categoryFilter);
      if (brokenOnly) q = q.eq("link_broken", true);
      if (debouncedSearch.trim()) q = q.or(`title.ilike.%${debouncedSearch.trim()}%,brand.ilike.%${debouncedSearch.trim()}%`);
      const { data, error } = await q;
      if (error) throw error;
      const products = (data ?? []) as ProductRow[];
      const ids = products.map((p) => p.id);
      if (ids.length === 0) return products;
      const { data: reports } = await supabase
        .from("product_reports")
        .select("product_id")
        .in("product_id", ids)
        .eq("status", "open");
      const counts = new Map<string, number>();
      (reports ?? []).forEach((r: { product_id: string }) => {
        counts.set(r.product_id, (counts.get(r.product_id) ?? 0) + 1);
      });
      const withCounts = products.map((p) => ({ ...p, open_reports: counts.get(p.id) ?? 0 }));
      return reportsOnly ? withCounts.filter((p) => (p.open_reports ?? 0) > 0) : withCounts;
    },
  });

  const openReportsCount = useQuery({
    queryKey: ["admin-product-reports-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("product_reports")
        .select("*", { count: "exact", head: true })
        .eq("status", "open");
      if (error) throw error;
      return count ?? 0;
    },
  });

  const brokenCount = useQuery({
    queryKey: ["admin-broken-links-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("products")
        .select("*", { count: "exact", head: true })
        .eq("link_broken", true);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const markFixed = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("products")
        .update({ link_broken: false, link_check_error: null, link_last_checked: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      qc.invalidateQueries({ queryKey: ["admin-broken-links-count"] });
    },
  });

  const toggleStock = useMutation({
    mutationFn: async ({ id, in_stock }: { id: string; in_stock: boolean }) => {
      const { error } = await supabase.from("products").update({ in_stock }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-products"] }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl">Products</h2>
        <p className="mt-1 text-sm text-muted-foreground">Search the catalog, edit descriptions with AI, and toggle stock.</p>
      </div>

      <div className="flex items-center gap-2 border-b border-border">
        <button
          onClick={() => { setReportsOnly(false); setBrokenOnly(false); }}
          className={`px-3 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px ${!reportsOnly && !brokenOnly ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
          All products
        </button>
        <button
          onClick={() => { setReportsOnly(true); setBrokenOnly(false); }}
          className={`px-3 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px inline-flex items-center gap-2 ${reportsOnly ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
          Reports
          {(openReportsCount.data ?? 0) > 0 && (
            <span className="bg-rose-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
              {openReportsCount.data}
            </span>
          )}
        </button>
        <button
          onClick={() => { setBrokenOnly(true); setReportsOnly(false); }}
          className={`px-3 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px inline-flex items-center gap-2 ${brokenOnly ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
        >
          Broken links
          {(brokenCount.data ?? 0) > 0 && (
            <span className="bg-rose-600 text-white text-[10px] px-1.5 py-0.5 rounded-full">
              {brokenCount.data}
            </span>
          )}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search title or brand"
          className="w-full sm:w-72 border border-border bg-card px-3 py-1.5 text-sm outline-none focus:border-foreground"
        />
        {(["all", "in", "out"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStockFilter(s)}
            className={`border px-3 py-1.5 text-xs uppercase tracking-widest ${
              stockFilter === s ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {s === "all" ? "All" : s === "in" ? "In stock" : "Out of stock"}
          </button>
        ))}
        <div className="relative">
          <select
            value={networkFilter}
            onChange={(e) => setNetworkFilter(e.target.value)}
            className="appearance-none border border-border bg-card px-3 py-1.5 pr-8 text-xs uppercase tracking-widest text-muted-foreground outline-none focus:border-foreground"
          >
            <option value="all">All Networks</option>
            <option value="Levanta">Levanta</option>
            <option value="Impact">Impact</option>
            <option value="Amazon">Amazon</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
        </div>
        <div className="relative">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="appearance-none border border-border bg-card px-3 py-1.5 pr-8 text-xs uppercase tracking-widest text-muted-foreground outline-none focus:border-foreground"
          >
            <option value="all">All Categories</option>
            {categories.data?.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
        </div>
        <span className="ml-auto text-xs text-muted-foreground">{list.data?.length ?? 0} shown</span>
      </div>

      <div className="overflow-x-auto border border-border bg-card -mx-4 sm:mx-0">
        <table className="w-full min-w-[860px] text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-2"></th>
              <th className="px-4 py-2">Product</th>
              <th className="px-4 py-2">Brand</th>
              <th className="px-4 py-2">Category</th>
              <th className="px-4 py-2 text-right">Price</th>
              <th className="px-4 py-2">Stock</th>
              <th className="px-4 py-2">Desc.</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!list.isLoading && (list.data?.length ?? 0) === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">No products match.</td></tr>
            )}
            {list.data?.map((p) => (
              <tr key={p.id} className={`border-t border-border ${!p.in_stock ? "opacity-60" : ""}`}>
                <td className="px-4 py-2"><img src={resolveImage(p.image_key)} alt={p.title} className="h-12 w-12 object-cover" /></td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <span className="line-clamp-1">{p.title}</span>
                    {(p.open_reports ?? 0) > 0 && (
                      <span title={`${p.open_reports} open report(s)`} className="inline-flex items-center justify-center h-5 min-w-5 px-1 bg-rose-600 text-white text-[10px] font-bold rounded-full">
                        !{p.open_reports! > 1 ? p.open_reports : ""}
                      </span>
                    )}
                    {p.link_broken && (
                      <span
                        title={p.link_check_error ?? "Broken link"}
                        className="inline-flex items-center px-1.5 py-0.5 bg-rose-600 text-white text-[10px] uppercase tracking-widest rounded"
                      >
                        Broken link
                      </span>
                    )}
                  </div>
                  <span className="block text-[10px] uppercase tracking-widest text-muted-foreground">{p.network}</span>
                </td>
                <td className="px-4 py-2 text-muted-foreground">{p.brand}</td>
                <td className="px-4 py-2 text-muted-foreground">{p.category}</td>
                <td className="px-4 py-2 text-right">{fmt(Number(p.price), p.currency)}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${p.in_stock ? "bg-emerald-100 text-emerald-900" : "bg-rose-100 text-rose-900"}`}>
                    {p.in_stock ? "In stock" : "Out"}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{p.description ? "✓" : "—"}</td>
                <td className="px-4 py-2">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => setEditing(p)}
                      title="Edit description"
                      className="border border-border px-2 py-1 hover:bg-foreground hover:text-background"
                    >
                      <Edit3 className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => toggleStock.mutate({ id: p.id, in_stock: !p.in_stock })}
                      disabled={toggleStock.isPending}
                      title={p.in_stock ? "Mark out of stock" : "Mark in stock"}
                      className="border border-border px-2 py-1 hover:bg-foreground hover:text-background disabled:opacity-50"
                    >
                      {p.in_stock ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    </button>
                    <a href={p.url} target="_blank" rel="noreferrer" className="border border-border px-2 py-1 hover:bg-foreground hover:text-background"><ExternalLink className="h-3 w-3" /></a>
                    {p.link_broken && (
                      <button
                        onClick={() => markFixed.mutate(p.id)}
                        disabled={markFixed.isPending}
                        title="Mark link fixed"
                        className="border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50"
                      >
                        Mark fixed
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <DescriptionDrawer product={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function DescriptionDrawer({ product, onClose }: { product: ProductRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState(product.description ?? "");
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { setText(product.description ?? ""); }, [product.id, product.description]);

  const reportsQ = useQuery({
    queryKey: ["admin-product-reports", product.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_reports")
        .select("id,product_id,reason,notes,status,created_at")
        .eq("product_id", product.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ProductReport[];
    },
  });

  const updateReport = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "resolved" | "dismissed" }) => {
      const { error } = await supabase.from("product_reports").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-product-reports", product.id] });
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      qc.invalidateQueries({ queryKey: ["admin-product-reports-count"] });
    },
  });

  const generate = useServerFn(generateProductDescription);
  const ai = useMutation({
    mutationFn: () => generate({ data: { productId: product.id, save: false } }),
    onSuccess: (res: { description: string }) => { setText(res.description); setError(null); },
    onError: (e: Error) => setError(e.message),
  });

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("products").update({ description: text }).eq("id", product.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    closeBtnRef.current?.focus();
    const dialog = dialogRef.current;
    if (!dialog) return;

    const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(selector)).filter(
        (el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true"
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    dialog.addEventListener("keydown", handleKeyDown);
    return () => dialog.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end bg-foreground/30"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-xl overflow-y-auto bg-background p-5 sm:p-8 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit product: ${product.title}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="editorial-eyebrow">{product.brand}</p>
            <h3 className="mt-1 font-serif text-2xl">{product.title}</h3>
          </div>
          <button ref={closeBtnRef} onClick={onClose} aria-label="Close product editor" className="border border-border p-2 hover:bg-card"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-6">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Description</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            placeholder="Write or generate an editorial description…"
            className="mt-2 w-full border border-border bg-card px-3 py-2 text-sm outline-none focus:border-foreground"
          />
        </div>

        {(reportsQ.data?.length ?? 0) > 0 && (
          <div className="mt-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Reports</p>
            <ul className="mt-2 space-y-2">
              {reportsQ.data!.map((r) => (
                <li key={r.id} className="border border-border bg-card p-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{REASON_LABEL[r.reason] ?? r.reason}</span>
                        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${r.status === "open" ? "bg-rose-100 text-rose-900" : "bg-muted text-muted-foreground"}`}>
                          {r.status}
                        </span>
                      </div>
                      {r.notes && <p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">{r.notes}</p>}
                      <p className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </p>
                    </div>
                    {r.status === "open" && (
                      <div className="flex flex-col gap-1 shrink-0">
                        <button
                          onClick={() => updateReport.mutate({ id: r.id, status: "resolved" })}
                          disabled={updateReport.isPending}
                          className="border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background disabled:opacity-50"
                        >
                          Mark resolved
                        </button>
                        <button
                          onClick={() => updateReport.mutate({ id: r.id, status: "dismissed" })}
                          disabled={updateReport.isPending}
                          className="border border-border px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-card disabled:opacity-50"
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && <p className="mt-3 text-xs text-rose-700">{error}</p>}

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <button
            onClick={() => ai.mutate()}
            disabled={ai.isPending}
            className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-widest hover:bg-card disabled:opacity-50"
          >
            {ai.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {text ? "Regenerate with AI" : "Generate with AI"}
          </button>
          <div className="ml-auto flex gap-2">
            <button onClick={onClose} className="border border-border px-4 py-2 text-xs uppercase tracking-widest hover:bg-card">Cancel</button>
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background hover:bg-accent disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
